import type { LucideIcon } from 'lucide-react';
import { BookOpen, FileText, GraduationCap, HandCoins, Inbox, LayoutGrid, NotebookPen, Presentation, Users, Wallet } from 'lucide-react';
import { can, type MemberRole, type Permission } from '@repo/shared-types';

/** One source for every nav surface (sidebar today; bottom tab bar in P0.4;
 *  sidebar v2 in P2.5). Labels are i18n pointers — the renderer resolves them. */
export type NavItemDef = {
  key: 'workbench' | 'classes' | 'students' | 'monthlyReviews' | 'clients' | 'subjects' | 'invoices' | 'bookkeeping' | 'payroll' | 'unmatched';
  href: string;
  icon: LucideIcon;
  label: { ns: string; key: string };
  perm?: Permission;
  badge?: 'unmatchedPayments';
  /** One of the phone bottom tab bar's four daily slots (P0.4). */
  mobilePrimary?: boolean;
  /** Short label for the tab bar, where the sidebar's full label doesn't fit. */
  mobileLabel?: { ns: string; key: string };
};

export type NavGroupDef = {
  key: 'class' | 'finance';
  label: { ns: 'common'; key: string };
  items: NavItemDef[];
};

export const NAV_GROUPS: NavGroupDef[] = [
  {
    key: 'class',
    label: { ns: 'common', key: 'nav.groupClass' },
    items: [
      { key: 'workbench', href: '/', icon: LayoutGrid, label: { ns: 'common', key: 'nav.dashboard' }, mobilePrimary: true, mobileLabel: { ns: 'common', key: 'nav.tab.today' } },
      // THI-267: "Thời khóa biểu" -> "Lớp học" (spec §5, handoff decision 1).
      // `presentation`, not `calendar-days` — keeping the calendar icon would
      // keep the exact mix-up ("khung giờ" vs "buổi học") the rename exists
      // to fix. Position and mobile split unchanged.
      { key: 'classes', href: '/classes', icon: Presentation, label: { ns: 'scheduling', key: 'nav.classes' }, mobilePrimary: true, mobileLabel: { ns: 'common', key: 'nav.tab.classes' } },
      { key: 'students', href: '/students', icon: GraduationCap, label: { ns: 'students', key: 'nav.students' }, perm: 'manage:students', mobilePrimary: true, mobileLabel: { ns: 'common', key: 'nav.tab.students' } },
      // THI-189: no `perm` — every role that writes session notes may also
      // write monthly reviews (a MEMBER's daily job, same rule as the
      // workbench nudge in useAttentionItems).
      // THI-193 CD review item 6: NotebookPen, not CalendarCheck — the
      // latter is also chốt sổ's icon (AttentionCard's `chot` row), and two
      // sidebar-adjacent surfaces sharing a calendar+tick icon read as the
      // same feature. AttentionCard already uses NotebookPen for this row.
      { key: 'monthlyReviews', href: '/monthly-reviews', icon: NotebookPen, label: { ns: 'progress', key: 'monthlyReviewsIndex.title' } },
      // Students + Clients are admin surfaces (THI-129): the roster carries payer
      // contact details and the client list is the payer record itself, so both
      // the nav item and the route are perm-gated. Subjects stays open — a
      // MEMBER teaches them and no money is on that screen.
      { key: 'clients', href: '/clients', icon: Users, label: { ns: 'common', key: 'nav.clients' }, perm: 'manage:clients' },
      // Not `mobilePrimary`: for an admin the four daily tabs are taken. It
      // carries a `mobileLabel` anyway because `splitMobileNav` promotes it
      // into the tab bar for roles whose tabs don't fill up (MEMBER).
      { key: 'subjects', href: '/subjects', icon: BookOpen, label: { ns: 'students', key: 'nav.subjects' }, mobileLabel: { ns: 'students', key: 'nav.subjects' } },
    ],
  },
  {
    key: 'finance',
    label: { ns: 'common', key: 'nav.groupFinance' },
    items: [
      { key: 'invoices', href: '/invoices', icon: FileText, label: { ns: 'common', key: 'nav.invoices' }, perm: 'manage:invoices', mobilePrimary: true, mobileLabel: { ns: 'common', key: 'nav.tab.invoices' } },
      { key: 'unmatched', href: '/payments/unmatched', icon: Inbox, label: { ns: 'payments', key: 'inbox.navLink' }, perm: 'manage:invoices', badge: 'unmatchedPayments' },
      { key: 'bookkeeping', href: '/bookkeeping', icon: Wallet, label: { ns: 'common', key: 'nav.bookkeeping' }, perm: 'manage:bookkeeping' },
      { key: 'payroll', href: '/payroll', icon: HandCoins, label: { ns: 'payroll', key: 'nav.payroll' }, perm: 'manage:members' },
    ],
  },
];

/** MEMBER (assistant teacher) only sees view + attendance pages — drop
 *  perm-gated items, then any group left empty (no bare group labels). */
export function filterNavForRole(groups: NavGroupDef[], role: MemberRole | null): NavGroupDef[] {
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.perm || (role !== null && can(role, i.perm))) }))
    .filter((g) => g.items.length > 0);
}

/** Today's rule: '/' matches exactly; anything else matches by prefix. */
export function isNavItemActive(href: string, pathname: string): boolean {
  return pathname === href || (href !== '/' && pathname.startsWith(href));
}

/** The tab bar holds this many nav tabs before the "Thêm" tab. */
const MOBILE_TAB_SLOTS = 4;

/** Phone navigation: the daily items become tabs, everything else the role may
 *  see goes into the "Thêm" sheet. Same perm filter as the sidebar.
 *
 *  A role whose perm-gated items leave the tab bar short (a MEMBER loses
 *  Học viên and Hoá đơn to THI-129, keeping two tabs) fills the empty slots
 *  from the remaining items, in nav order — otherwise the sheet exists to hold
 *  one orphan row while the bar sits half empty. Admin roles already fill all
 *  four slots, so their bar and sheet are unchanged by this. */
export function splitMobileNav(groups: NavGroupDef[], role: MemberRole | null): { primary: NavItemDef[]; more: NavItemDef[] } {
  const items = filterNavForRole(groups, role).flatMap((g) => g.items);
  const primary = items.filter((i) => i.mobilePrimary);
  const rest = items.filter((i) => !i.mobilePrimary);
  const promoted = rest.slice(0, Math.max(0, MOBILE_TAB_SLOTS - primary.length));
  return { primary: [...primary, ...promoted], more: rest.slice(promoted.length) };
}
